import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ETAPA_OPTIONS = [
  { v: 'nuevo', l: 'Nuevo' },
  { v: 'estudio', l: 'Estudio' },
  { v: 'mediacion', l: 'Mediación' },
  { v: 'en_gestion', l: 'En Gestión' },
  { v: 'oferta', l: 'Oferta' },
  { v: 'proceso_compra', l: 'Proceso de compra' },
  { v: 'adquirido', l: 'Adquirido' },
];

/**
 * Se muestra al completar la última tarea abierta de un activo en fase
 * "En Gestión": pregunta si crear otra tarea, cambiar de fase, o pasar el
 * activo a Standby/Cerrado.
 */
const TaskPhasePrompt = ({ open, assetId, currentEtapaVisual, onClose, onCreateTask, onDone }) => {
  const [mode, setMode] = useState(null); // 'fase' | 'estado' | null
  const [newFase, setNewFase] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => { setMode(null); setNewFase(''); };

  const handleClose = () => { reset(); onClose(); };

  const handleFaseConfirm = async () => {
    if (!newFase) { toast.error('Selecciona una fase'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/assets/${assetId}/quick-update`, { etapa_visual: newFase }, { withCredentials: true });
      toast.success('Fase actualizada');
      reset();
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar de fase');
    } finally {
      setSaving(false);
    }
  };

  const handleEstado = async (estado) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/assets/${assetId}/quick-update`, { estado }, { withCredentials: true });
      toast.success(estado === 'standby' ? 'Activo pasado a Standby' : 'Activo cerrado');
      reset();
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar el activo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={handleClose}
      data-testid="task-phase-prompt-overlay"
    >
      <div
        className="rounded-xl shadow-2xl max-w-md w-full p-5"
        style={{ background: 'var(--color-bg-elevated)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="task-phase-prompt-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-base" style={{ color: 'var(--color-text-primary)' }}>
            Última tarea pendiente completada
          </h3>
          <button onClick={handleClose} aria-label="Cerrar" data-testid="task-phase-prompt-close">
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>
        <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
          Este activo está En Gestión y ya no tiene tareas pendientes. ¿Qué quieres hacer?
        </p>

        {!mode && (
          <div className="space-y-2">
            <button onClick={onCreateTask} className="btn-primary w-full" data-testid="task-phase-prompt-create-task-btn">
              Crear otra tarea
            </button>
            <button onClick={() => setMode('fase')} className="w-full px-4 py-2 text-sm font-medium rounded border" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="task-phase-prompt-change-phase-btn">
              Cambiar de fase
            </button>
            <button onClick={() => setMode('estado')} className="w-full px-4 py-2 text-sm font-medium rounded border" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="task-phase-prompt-standby-btn">
              Pasar a Standby / Cerrar
            </button>
          </div>
        )}

        {mode === 'fase' && (
          <div className="space-y-3">
            <select className="input-base w-full" value={newFase} onChange={(e) => setNewFase(e.target.value)} data-testid="task-phase-prompt-fase-select">
              <option value="">Selecciona una fase</option>
              {ETAPA_OPTIONS.filter((o) => o.v !== currentEtapaVisual).map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMode(null)} className="px-3 py-2 text-sm rounded border" style={{ borderColor: 'var(--color-border)' }}>Volver</button>
              <button onClick={handleFaseConfirm} disabled={saving} className="btn-primary" data-testid="task-phase-prompt-fase-confirm-btn">
                {saving ? 'Guardando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}

        {mode === 'estado' && (
          <div className="space-y-2">
            <button onClick={() => handleEstado('standby')} disabled={saving} className="w-full px-4 py-2 text-sm font-semibold rounded bg-slate-600 text-white hover:bg-slate-700" data-testid="task-phase-prompt-standby-confirm-btn">
              Standby
            </button>
            <button onClick={() => handleEstado('cerrado')} disabled={saving} className="w-full px-4 py-2 text-sm font-semibold rounded bg-slate-800 text-white hover:bg-slate-900" data-testid="task-phase-prompt-cerrado-confirm-btn">
              Cerrado
            </button>
            <button onClick={() => setMode(null)} className="w-full px-3 py-2 text-sm rounded border" style={{ borderColor: 'var(--color-border)' }}>Volver</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskPhasePrompt;
